import { createReducer, on } from '@ngrx/store';
import { PurchasesOfferings, PurchasesStoreProduct } from '@revenuecat/purchases-capacitor';
import { CustomerInfo } from '@revenuecat/purchases-typescript-internal-esm';

import {
  compact,
  each,
  find,
  findIndex,
  flatten,
  intersection,
  isNull,
  isUndefined,
  map,
  orderBy,
  sortBy
} from 'lodash-es';
import { DateTime, Duration } from 'luxon';

import {
  PrimarySubscriptionPlanAccessAliasEnum,
  PrimarySubscriptionPlanEnum,
  StoreProductEnum,
  StripePurchaseEnum
} from '../../enums/monetization';
import { getMainRouteByPropertySearchType } from '../../helpers/property-profile';
import {
  MonetizationStripePrice,
  MonetizationStripeProduct,
  RevenueCatWebAPICustomerInfo,
  RevenueCatWebAPIOfferings,
  StripeSubscriptionModel
} from '../../typings/monetization';
import { InAppPurchaseProduct, StoreProduct, SubscriptionProduct } from '../../typings/system-configurations';
import * as fromMonetizationActions from '../actions/monetization.actions';
import * as fromPersistedStateActions from '../actions/persisted-state.actions';
import * as fromSettingActions from '../actions/setting.actions';
import * as fromUserActions from '../actions/user.actions';

export const monetizationFeatureKey = 'monetization';

export const paymentPlanRules: {
  accessAlias: PrimarySubscriptionPlanAccessAliasEnum;
  entitlement: string;
  offering: string;
  plan: PrimarySubscriptionPlanEnum;
}[] = [
  {
    accessAlias: PrimarySubscriptionPlanAccessAliasEnum.A,
    entitlement: 'plan_a_features',
    offering: 'plan_a_access',
    plan: PrimarySubscriptionPlanEnum.A
  },
  {
    accessAlias: PrimarySubscriptionPlanAccessAliasEnum.A,
    entitlement: 'plan_a_features',
    offering: 'plan_a_access_test',
    plan: PrimarySubscriptionPlanEnum.ATest
  },
  {
    accessAlias: PrimarySubscriptionPlanAccessAliasEnum.B,
    entitlement: 'plan_b_features',
    offering: 'plan_b_access',
    plan: PrimarySubscriptionPlanEnum.B
  },
  {
    accessAlias: PrimarySubscriptionPlanAccessAliasEnum.B,
    entitlement: 'plan_b_features',
    offering: 'plan_b_access_test',
    plan: PrimarySubscriptionPlanEnum.BTest
  },
  {
    accessAlias: PrimarySubscriptionPlanAccessAliasEnum.C,
    entitlement: 'plan_c_features',
    offering: 'plan_c_access',
    plan: PrimarySubscriptionPlanEnum.C
  },
  {
    accessAlias: PrimarySubscriptionPlanAccessAliasEnum.C,
    entitlement: 'plan_c_features',
    offering: 'plan_c_access_test',
    plan: PrimarySubscriptionPlanEnum.CTest
  }
];

export interface State {
  isEnabled: boolean;
  mobileCustomerInfo: CustomerInfo | null;
  mobilePurchasesOfferings: PurchasesOfferings | null;
  phoneAndEmailPurchaseEnabled: boolean;
  primarySubscriptionAccessAlias: PrimarySubscriptionPlanAccessAliasEnum | null;
  primarySubscriptionDescription: string;
  primarySubscriptionEntitlement: string | null;
  primarySubscriptionIdentifier: string | null;
  primarySubscriptionIsActive: boolean;
  primarySubscriptionIsEnabled: boolean;
  primarySubscriptionIsPaymentLoading: boolean;
  primarySubscriptionIsPaymentSuccess: boolean;
  primarySubscriptionName: string;
  primarySubscriptionOfferingsIdentifier: string | null;
  primarySubscriptionPlan: PrimarySubscriptionPlanEnum | null;
  primarySubscriptionPlatformProductIdentifier: string | null;
  primarySubscriptionPriceText: string;
  primarySubscriptionPurchasesStoreProduct: PurchasesStoreProduct | null;
  primarySubscriptionWebPriceId: string | null;
  purchasePhoneAndEmailPreviewFarmId: string | null;
  purchasePhoneAndEmailPreviewPropertyIds: string[];
  storeInAppPurchases: InAppPurchaseProduct[];
  storePlatformProductIdentifiers: string[];
  storeProducts: StoreProduct[];
  storeSelectedProductDescription: string | null;
  storeSelectedProductInternalIdentifier: string | null;
  storeSelectedProductName: string;
  storeSelectedProductPriceText: string;
  storeSelectedProductType: StripePurchaseEnum;
  storeSelectedProductWebPriceId: string | null;
  storeSelectedPurchasesStoreProduct: PurchasesStoreProduct | null;
  storeSubscriptions: SubscriptionProduct[];
  storeWebPriceId: string | null;
  virtualCurrency: number;
  webCustomerInfo: RevenueCatWebAPICustomerInfo | null;
  webPurchasesOfferings: RevenueCatWebAPIOfferings | null;
  webPurchasesOfferingsPlatformProductIdentifiers: string[];
  webRedirectBackUrl: string;
  webStoreProductsDetails: MonetizationStripeProduct[];
  webSubscriptionsList: StripeSubscriptionModel[];
}

export const initialState: State = {
  isEnabled: false,
  mobileCustomerInfo: null,
  mobilePurchasesOfferings: null,
  phoneAndEmailPurchaseEnabled: false,
  primarySubscriptionAccessAlias: null,
  primarySubscriptionDescription: 'N/A',
  primarySubscriptionEntitlement: null,
  primarySubscriptionIdentifier: null,
  primarySubscriptionIsActive: false,
  primarySubscriptionIsEnabled: false,
  primarySubscriptionIsPaymentLoading: false,
  primarySubscriptionIsPaymentSuccess: false,
  primarySubscriptionName: 'N/A',
  primarySubscriptionOfferingsIdentifier: null,
  primarySubscriptionPlan: null,
  primarySubscriptionPlatformProductIdentifier: null,
  primarySubscriptionPriceText: 'N/A',
  primarySubscriptionPurchasesStoreProduct: null,
  primarySubscriptionWebPriceId: null,
  purchasePhoneAndEmailPreviewFarmId: null,
  purchasePhoneAndEmailPreviewPropertyIds: [],
  storeInAppPurchases: [],
  storePlatformProductIdentifiers: [],
  storeProducts: [],
  storeSelectedProductDescription: null,
  storeSelectedProductInternalIdentifier: null,
  storeSelectedProductName: 'N/A',
  storeSelectedProductPriceText: 'N/A',
  storeSelectedProductType: StripePurchaseEnum.Payment,
  storeSelectedProductWebPriceId: null,
  storeSelectedPurchasesStoreProduct: null,
  storeSubscriptions: [],
  storeWebPriceId: null,
  virtualCurrency: 0,
  webCustomerInfo: null,
  webPurchasesOfferings: null,
  webPurchasesOfferingsPlatformProductIdentifiers: [],
  webRedirectBackUrl: '/',
  webStoreProductsDetails: [],
  webSubscriptionsList: []
};

export function sortStoreProducts(data: StoreProduct[]) {
  return sortBy(orderBy(data, ['type']), (product) => {
    const index = findIndex(
      [
        'VIRTUAL_CURRENCY_20',
        'VIRTUAL_CURRENCY_40',
        'VIRTUAL_CURRENCY_100',
        'VIRTUAL_CURRENCY_200',
        'VIRTUAL_CURRENCY_300',
        'VIRTUAL_CURRENCY_400',
        'VIRTUAL_CURRENCY_1000',
        'VIRTUAL_CURRENCY_2000'
      ],
      (item) => product.internalIdentifier === item
    );
    return index === -1 ? data.length : index;
  });
}

export function parseWebPriceDetails(prices: MonetizationStripePrice[]) {
  const result: {
    priceText: string;
    webPriceId: string | null;
  } = {
    priceText: 'N/A',
    webPriceId: null
  };
  if (prices.length) {
    const priceData = prices.find((price) => price.active);
    if (priceData) {
      const numberFormat = new Intl.NumberFormat('en-US', {
        currency: (priceData.currency ?? 'USD').toUpperCase(),
        style: 'currency'
      });
      if (priceData.unit_amount) {
        result.priceText = numberFormat.format(priceData.unit_amount / 100);
      }
      if (priceData.type === 'recurring' && priceData.recurring) {
        const period = Duration.fromObject({
          [priceData.recurring.interval]: priceData.recurring.interval_count
        });
        if (period.isValid) {
          result.priceText += ` per ${period.as('months') > 1 ? period.as('months') : ''} month${period.as('months') > 1 ? 's' : ''}`;
        }
      }
      result.webPriceId = priceData.id;
    }
  }
  return result;
}

export function parseMobilePriceDetails(product: PurchasesStoreProduct) {
  const result: {
    priceText: string;
    webPriceId: string | null;
  } = {
    priceText: 'N/A',
    webPriceId: null
  };
  if (product.price) {
    const numberFormat = new Intl.NumberFormat('en-US', {
      currency: (product.currencyCode ?? 'USD').toUpperCase(),
      style: 'currency'
    });
    result.priceText = numberFormat.format(product.price);
    if (!isNull(product.subscriptionPeriod)) {
      const period = Duration.fromISO(product.subscriptionPeriod);
      if (period.isValid) {
        result.priceText += ` per ${period.as('months') > 1 ? period.as('months') : ''} month${period.as('months') > 1 ? 's' : ''}`;
      }
    }
  }
  return result;
}

export function updateStorePlatformProductIdentifiers(state: State): State {
  const newState = { ...state, storePlatformProductIdentifiers: initialState.storePlatformProductIdentifiers };
  const inAppPurchaseIdentifiers = compact(
    flatten(map(newState.storeInAppPurchases, (item) => (item.visibleInStore ? item.storeIdentifiers : null)))
  );
  const subscriptionsIdentifiers = flatten(map(newState.storeSubscriptions, (item) => item.storeIdentifiers));
  let purchaseOfferingsIdentifiers: string[] = [];
  if (newState.webPurchasesOfferings) {
    purchaseOfferingsIdentifiers = flatten(
      map(newState.webPurchasesOfferings.offerings, (offering) =>
        map(offering.packages, (pack) => pack.platform_product_identifier)
      )
    );
  } else if (newState.mobilePurchasesOfferings) {
    purchaseOfferingsIdentifiers = flatten(
      map(newState.mobilePurchasesOfferings.all, (offering) =>
        map(offering.availablePackages, (pack) => pack.product.identifier)
      )
    );
  }
  newState.storePlatformProductIdentifiers = intersection(purchaseOfferingsIdentifiers, [
    ...inAppPurchaseIdentifiers,
    ...subscriptionsIdentifiers
  ]);
  return newState;
}

export function updateStoreProductsDetails(state: State): State {
  const storeProducts: StoreProduct[] = [];

  each(state.storePlatformProductIdentifiers, (platformProductIdentifier) => {
    const product = find(state.storeInAppPurchases, (inAppPurchasesProduct) =>
      inAppPurchasesProduct.storeIdentifiers.includes(platformProductIdentifier)
    );
    let subscription: SubscriptionProduct | undefined;
    if (!product) {
      subscription = find(state.storeSubscriptions, (subscriptions) =>
        subscriptions.storeIdentifiers.includes(platformProductIdentifier)
      );
    }
    let productName = 'N/A';
    let isOwned = false;

    let priceDetails: {
      priceText: string;
      webPriceId: string | null;
    } | null = null;

    if (state.webPurchasesOfferings || state.webCustomerInfo) {
      let entitlementIdentifier: string | null = null;
      each(state.webPurchasesOfferings?.offerings, (offering) =>
        each(offering.packages, (pack) => {
          if (pack.platform_product_identifier === platformProductIdentifier) {
            entitlementIdentifier = offering.identifier;
          }
        })
      );
      const productDescription = find(
        state.webStoreProductsDetails,
        (webStoreProductDetails) => webStoreProductDetails.id === platformProductIdentifier
      );
      if (productDescription) {
        productName = productDescription.name;
        priceDetails = parseWebPriceDetails(productDescription.prices);
        each(state.webCustomerInfo?.subscriber.entitlements, (entitlement, identifier) => {
          if (identifier === entitlementIdentifier) {
            isOwned = true;
          }
        });
      }
    } else if (state.mobilePurchasesOfferings || state.mobileCustomerInfo) {
      let entitlementIdentifier: string | null = null;
      each(state.mobilePurchasesOfferings?.all, (offering, identifier) =>
        each(offering.availablePackages, (pack) => {
          if (pack.product.identifier === platformProductIdentifier) {
            entitlementIdentifier = identifier;
            productName = pack.product.title;
            priceDetails = parseMobilePriceDetails(pack.product);
          }
        })
      );
      each(state.mobileCustomerInfo?.entitlements.active, (entitlement) => {
        if (entitlement.identifier === entitlementIdentifier) {
          isOwned = true;
        }
      });
    }

    if (product && priceDetails) {
      storeProducts.push({
        internalIdentifier: product.identifier,
        isOwned,
        name: productName,
        platformProductIdentifier,
        priceText: priceDetails.priceText,
        type: product.consumable ? StoreProductEnum.Consumable : StoreProductEnum.NonConsumable,
        webPriceId: priceDetails.webPriceId
      });
    } else if (subscription && priceDetails) {
      storeProducts.push({
        internalIdentifier: subscription.identifier,
        isOwned,
        name: productName,
        platformProductIdentifier,
        priceText: priceDetails.priceText,
        type: StoreProductEnum.Subscription,
        webPriceId: priceDetails.webPriceId
      });
    }
  });
  return { ...state, storeProducts: sortStoreProducts(storeProducts) };
}
export function updatePrimarySubscriptionActiveStatus(state: State): State {
  const newState = { ...state, primarySubscriptionIsActive: initialState.primarySubscriptionIsActive };
  if (newState.primarySubscriptionEntitlement) {
    if (newState.mobileCustomerInfo) {
      const entitlementsList = newState.mobileCustomerInfo?.entitlements.active || {};
      const activeEntitlement = entitlementsList[newState.primarySubscriptionEntitlement];
      if (activeEntitlement) {
        newState.primarySubscriptionIsActive = activeEntitlement.isActive;
      }
    } else if (newState.webCustomerInfo) {
      const entitlementsList = newState.webCustomerInfo?.subscriber?.entitlements || {};
      const activeEntitlement = entitlementsList[newState.primarySubscriptionEntitlement];
      if (activeEntitlement) {
        const checkExpiry =
          activeEntitlement.expires_date &&
          DateTime.now() < DateTime.fromJSDate(new Date(activeEntitlement.expires_date));
        const graceDate = activeEntitlement.grace_period_expires_date;
        const checkGrace = !!graceDate && DateTime.now() < DateTime.fromJSDate(new Date(graceDate));
        newState.primarySubscriptionIsActive = checkExpiry || checkGrace;
      }
    }
  }
  return newState;
}

export function updatePrimarySubscriptionPlatformProductIdentifier(state: State): State {
  const newState = {
    ...state,
    primarySubscriptionIdentifier: initialState.primarySubscriptionIdentifier,
    primarySubscriptionPlatformProductIdentifier: initialState.primarySubscriptionPlatformProductIdentifier
  };
  if (newState.primarySubscriptionOfferingsIdentifier) {
    if (newState.mobilePurchasesOfferings) {
      newState.primarySubscriptionName = initialState.primarySubscriptionName;
      newState.primarySubscriptionPriceText = initialState.primarySubscriptionPriceText;
      newState.primarySubscriptionPurchasesStoreProduct = initialState.primarySubscriptionPurchasesStoreProduct;
      const offering = find(
        newState.mobilePurchasesOfferings.all,
        (item) => item.identifier === newState.primarySubscriptionOfferingsIdentifier
      );
      if (offering) {
        const primarySubscriptionPackage = offering?.availablePackages[0];
        if (primarySubscriptionPackage) {
          newState.primarySubscriptionIdentifier = primarySubscriptionPackage.identifier;
          newState.primarySubscriptionPlatformProductIdentifier = primarySubscriptionPackage.product.identifier;
          newState.primarySubscriptionName = primarySubscriptionPackage.product.title;
          newState.primarySubscriptionPurchasesStoreProduct = { ...primarySubscriptionPackage.product };
          newState.primarySubscriptionPriceText = parseMobilePriceDetails(primarySubscriptionPackage.product).priceText;
        }
      }
    } else if (newState.webPurchasesOfferings) {
      const offering = find(
        newState.webPurchasesOfferings.offerings,
        (item) => item.identifier === newState.primarySubscriptionOfferingsIdentifier
      );
      if (offering) {
        const primarySubscriptionPackage = offering?.packages[0];
        if (primarySubscriptionPackage) {
          newState.primarySubscriptionIdentifier = primarySubscriptionPackage.identifier;
          newState.primarySubscriptionPlatformProductIdentifier =
            primarySubscriptionPackage.platform_product_identifier;
        }
      }
    }
  }
  return newState;
}

export const reducer = createReducer(
  initialState,
  on(
    fromSettingActions.loadSettingsSuccess,
    fromSettingActions.updateReferralCodeSuccess,
    fromSettingActions.refreshReferralCodeSuccess,
    fromPersistedStateActions.migratePersistedOldStateReferralCodeSuccess,
    (state, { payload: { data } }) => {
      let newState = {
        ...state,
        primarySubscriptionAccessAlias: initialState.primarySubscriptionAccessAlias,
        primarySubscriptionEntitlement: initialState.primarySubscriptionEntitlement,
        primarySubscriptionIsEnabled: initialState.primarySubscriptionIsEnabled,
        primarySubscriptionOfferingsIdentifier: initialState.primarySubscriptionOfferingsIdentifier,
        primarySubscriptionPlan: initialState.primarySubscriptionPlan
      };
      if (!isUndefined(data.products.subscriptionPlan)) {
        const primarySubscriptionPlanRules =
          paymentPlanRules.find((plan) => plan.plan === data.products.subscriptionPlan) || null;
        if (primarySubscriptionPlanRules) {
          newState.primarySubscriptionAccessAlias = primarySubscriptionPlanRules.accessAlias;
          newState.primarySubscriptionEntitlement = primarySubscriptionPlanRules.entitlement;
          newState.primarySubscriptionIsEnabled = true;
          newState.primarySubscriptionOfferingsIdentifier = primarySubscriptionPlanRules.offering;
          newState.primarySubscriptionPlan = primarySubscriptionPlanRules.plan;
        }
      }
      newState.storeInAppPurchases = data.products.inAppPurchases;
      newState.storeSubscriptions = data.products.subscriptions;
      newState.phoneAndEmailPurchaseEnabled = data.office.phoneAndEmailPurchase;
      newState.isEnabled = newState.primarySubscriptionIsEnabled || newState.phoneAndEmailPurchaseEnabled;
      newState = updatePrimarySubscriptionPlatformProductIdentifier(newState);
      newState = updatePrimarySubscriptionActiveStatus(newState);
      newState = updateStorePlatformProductIdentifiers(newState);
      newState = updateStoreProductsDetails(newState);
      return newState;
    }
  ),
  on(
    fromUserActions.loadMobileUserSuccess,
    fromPersistedStateActions.migratePersistedOldStateUserDataSuccess,
    (state, { payload }) => ({
      ...state,
      ...{
        virtualCurrency: payload.data.virtualCurrency
      }
    })
  ),
  on(fromMonetizationActions.updateMobilePurchasesOfferings, (state, { payload: { purchasesOfferings } }) =>
    updateStoreProductsDetails(
      updateStorePlatformProductIdentifiers(
        updatePrimarySubscriptionActiveStatus(
          updatePrimarySubscriptionPlatformProductIdentifier({
            ...state,
            mobilePurchasesOfferings: purchasesOfferings
          })
        )
      )
    )
  ),
  on(fromMonetizationActions.loadPrimarySubscriptionWebDetailsSuccess, (state, { data }) => {
    const newState = {
      ...state,
      primarySubscriptionName: data.name,
      primarySubscriptionPriceText: initialState.primarySubscriptionPriceText,
      primarySubscriptionWebPriceId: initialState.primarySubscriptionWebPriceId
    };
    if (data?.prices.length) {
      const result = parseWebPriceDetails(data.prices);
      newState.primarySubscriptionPriceText = result.priceText;
      newState.primarySubscriptionWebPriceId = result.webPriceId;
    }
    return newState;
  }),
  on(fromMonetizationActions.loadPrimarySubscriptionWebDetailsFailure, (state) => ({
    ...state,
    primarySubscriptionName: initialState.primarySubscriptionName,
    primarySubscriptionPriceText: initialState.primarySubscriptionPriceText
  })),
  on(fromMonetizationActions.updateSelectedStoreWebProductDetails, (state, { payload }) => {
    let storeSelectedPurchasesStoreProduct: PurchasesStoreProduct | null = null;
    each(state.mobilePurchasesOfferings?.all, (offering) => {
      each(offering.availablePackages, (pack) => {
        if (pack.product.identifier === payload.platformProductIdentifier) {
          storeSelectedPurchasesStoreProduct = pack.product;
        }
      });
    });
    return {
      ...state,
      storeSelectedProductDescription: initialState.storeSelectedProductDescription,
      storeSelectedProductInternalIdentifier: payload.internalIdentifier,
      storeSelectedProductName: payload.name,
      storeSelectedProductPriceText: payload.priceText,
      storeSelectedProductType:
        payload.type === StoreProductEnum.Subscription ? StripePurchaseEnum.Subscription : StripePurchaseEnum.Payment,
      storeSelectedProductWebPriceId: payload.webPriceId,
      storeSelectedPurchasesStoreProduct
    };
  }),
  on(fromMonetizationActions.updateSelectedStoreWebProductDetailsSuccess, (state, { payload }) => ({
    ...state,
    storeSelectedProductDescription: payload.description
  })),
  on(fromMonetizationActions.logUserPurchaseSuccess, fromMonetizationActions.logUserPurchaseFailure, (state) => ({
    ...state,
    storeSelectedProductDescription: initialState.storeSelectedProductDescription,
    storeSelectedProductInternalIdentifier: initialState.storeSelectedProductInternalIdentifier,
    storeSelectedProductName: initialState.storeSelectedProductName,
    storeSelectedProductPriceText: initialState.storeSelectedProductPriceText,
    storeSelectedProductType: initialState.storeSelectedProductType,
    storeSelectedProductWebPriceId: initialState.storeSelectedProductWebPriceId,
    storeSelectedPurchasesStoreProduct: initialState.storeSelectedPurchasesStoreProduct
  })),
  on(fromMonetizationActions.loadPrimarySubscriptionDetailsSuccess, (state, { data: { description } }) => ({
    ...state,
    primarySubscriptionDescription: description
  })),
  on(fromMonetizationActions.loadPrimarySubscriptionDetailsFailure, (state) => ({
    ...state,
    primarySubscriptionDescription: initialState.primarySubscriptionDescription
  })),
  on(
    fromMonetizationActions.persistAndRedirectToPurchase,
    (state, { payload: { redirectBackOnSuccess, redirectBackUrl, searchType } }) => {
      const newState = { ...state };
      newState.webRedirectBackUrl = redirectBackUrl;
      if (!redirectBackOnSuccess) {
        newState.purchasePhoneAndEmailPreviewFarmId = null;
        newState.purchasePhoneAndEmailPreviewPropertyIds = [];
      } else if (redirectBackOnSuccess && newState.purchasePhoneAndEmailPreviewFarmId) {
        const mainPath = getMainRouteByPropertySearchType(searchType);
        newState.webRedirectBackUrl = `${mainPath}/${state.purchasePhoneAndEmailPreviewFarmId}/view`;
      }
      return newState;
    }
  ),
  on(
    fromMonetizationActions.updateMobileCustomerInfo,
    fromMonetizationActions.paywallMobilePurchaseSuccess,
    (state, { payload: { customerInfo } }) =>
      updateStoreProductsDetails(
        updatePrimarySubscriptionActiveStatus({
          ...state,
          mobileCustomerInfo: customerInfo
        })
      )
  ),
  on(fromMonetizationActions.loadWebPurchasesOfferingsSuccess, (state, { data: { purchasesOfferings } }) =>
    updateStorePlatformProductIdentifiers({
      ...state,
      webPurchasesOfferings: purchasesOfferings
    })
  ),
  on(fromMonetizationActions.loadWebCustomerInfoSuccess, (state, { data: { customerInfo } }) =>
    updateStoreProductsDetails(
      updatePrimarySubscriptionActiveStatus(
        updatePrimarySubscriptionPlatformProductIdentifier({
          ...state,
          webCustomerInfo: customerInfo
        })
      )
    )
  ),

  on(fromMonetizationActions.loadStoreWebDetailsSuccess, (state, { payload }) =>
    updateStoreProductsDetails({
      ...state,
      webStoreProductsDetails: payload
    })
  ),
  on(fromMonetizationActions.loadSubscriptions, (state) => ({
    ...state,
    webSubscriptionsList: initialState.webSubscriptionsList
  })),
  on(fromMonetizationActions.purchasePhoneAndEmailPreview, (state, { payload }) => ({
    ...state,
    purchasePhoneAndEmailPreviewFarmId: payload.farmId,
    purchasePhoneAndEmailPreviewPropertyIds: payload.propertyIds
  })),
  on(fromMonetizationActions.loadSubscriptionsSuccess, (state, { payload }) => ({
    ...state,
    webSubscriptionsList: payload
  })),
  on(fromPersistedStateActions.persistedStateReset, fromPersistedStateActions.loadPersistedStateFailure, (state) => ({
    ...state,
    ...initialState
  })),
  on(fromPersistedStateActions.loadPersistedStateSuccess, (state, { payload }) => ({
    ...state,
    ...payload[monetizationFeatureKey]
  }))
);
